import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useState, useEffect, useRef } from 'react';
import { useImpersonation } from '@/contexts/ImpersonationContext';
import { logger } from '@/lib/logger';
import { evaluateEntitlement } from '@/lib/subscriptionEntitlement';

interface PlanLimits {
  plan_tier: string;
  max_active_norths: number | null;
  max_team_members: number | null;
  includes_custom_branding: boolean;
  includes_white_label: boolean;
  includes_api_access: boolean;
  max_cmas_per_month: number | null;
  max_property_reports_per_month: number | null;
}

interface TeamUsage {
  norths_created: number;
  cmas_this_month: number;
  property_reports_this_month: number;
}

interface TeamSubscription {
  plan_tier: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  trial_ends_at: string | null;
}

interface TeamData {
  teamId: string;
  planLimits: PlanLimits | null;
  usage: TeamUsage | null;
  subscription: TeamSubscription | null;
}

async function fetchTeamData(effectiveUserId: string): Promise<TeamData> {
  if (!effectiveUserId) throw new Error('Not authenticated');

  // Get user's team
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('team_id, user_id')
    .eq('user_id', effectiveUserId)
    .single();

  if (profileError) {
    logger.error('Error fetching profile in fetchTeamData:', profileError);
    throw new Error('Failed to fetch user profile');
  }

  if (!profile?.team_id) {
    logger.warn('No team found for user:', effectiveUserId);
    throw new Error('No team found');
  }

  if (profile.user_id !== effectiveUserId) {
    logger.error('User ID mismatch in fetchTeamData!', {
      requested: effectiveUserId,
      returned: profile.user_id
    });
    throw new Error('User ID mismatch - data integrity error');
  }

  // Fetch subscription, team profiles in parallel
  const [subResult, teamProfilesResult] = await Promise.all([
    supabase
      .from('team_subscriptions' as any)
      .select('*')
      .eq('team_id', profile.team_id)
      .maybeSingle(),
    supabase
      .from('profiles')
      .select('user_id')
      .eq('team_id', profile.team_id)
  ]);

  const subData = subResult.data;
  const teamProfiles = teamProfilesResult.data;
  const teamUserIds = teamProfiles?.map(p => p.user_id) || [];

  // Calculate start of current calendar month
  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const monthStartIso = monthStart.toISOString();

  // Fetch plan_limits, norths count, CMA count, and Buyer Report count in parallel
  const [limitsResult, northsResult, cmaResult, propertyReportResult] = await Promise.all([
    subData
      ? supabase
          .from('plan_limits' as any)
          .select('*')
          .eq('plan_tier', (subData as any).plan_tier)
          .maybeSingle()
      : Promise.resolve({ data: null }),
    teamUserIds.length > 0
      ? supabase
          .from('norths')
          .select('*', { count: 'exact', head: true })
          .in('user_id', teamUserIds)
          .eq('is_published', true)
          .eq('is_draft', false)
      : Promise.resolve({ count: 0 }),
    profile.team_id
      ? supabase
          .from('cmas' as any)
          .select('id')
          .eq('team_id', profile.team_id)
          .gte('created_at', monthStartIso)
      : Promise.resolve({ data: [], error: null }),
    profile.team_id
      ? supabase
          .from('property_reports' as any)
          .select('id')
          .eq('team_id', profile.team_id)
          .gte('created_at', monthStartIso)
      : Promise.resolve({ data: [], error: null }),
  ]);

  if ((cmaResult as any).error) {
    logger.error('Error fetching CMA count:', (cmaResult as any).error);
  }
  if ((propertyReportResult as any).error) {
    logger.error('Error fetching property report count:', (propertyReportResult as any).error);
  }

  const planLimits: PlanLimits | null = (limitsResult as any).data || null;
  const northsCount = (northsResult as any).count || 0;

  const finalUsage: TeamUsage = {
    norths_created: northsCount,
    cmas_this_month: ((cmaResult as any).data?.length ?? 0),
    property_reports_this_month: ((propertyReportResult as any).data?.length ?? 0),
  };

  return {
    teamId: profile.team_id,
    planLimits,
    usage: finalUsage,
    subscription: subData as any,
  };
}

export function useTeamUsageQuery() {
  const queryClient = useQueryClient();
  const { getEffectiveUserId, isImpersonating, impersonatedUser } = useImpersonation();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<string>('');

  const [effectiveUserId, setEffectiveUserId] = useState<string | null>(null);
  const previousUserIdRef = useRef<string | null>(null);

  // Resolve the effective user ID on mount and when impersonation changes
  useEffect(() => {
    let mounted = true;

    const resolveUserId = async () => {
      try {
        // For impersonation, use the impersonated user ID directly (no network call)
        if (isImpersonating && impersonatedUser?.id) {
          if (mounted) {
            const newId = impersonatedUser.id;
            if (previousUserIdRef.current !== newId) {
              // User changed (impersonation started/switched) — clear stale cache
              if (previousUserIdRef.current !== null) {
                queryClient.removeQueries({ queryKey: ['team-usage'] });
              }
              previousUserIdRef.current = newId;
              setEffectiveUserId(newId);
            }
          }
          return;
        }

        // For normal users, use getSession() (cached, no network call) instead of getUser() (network call)
        const { data: { session } } = await supabase.auth.getSession();
        const userId = session?.user?.id || null;

        if (mounted && userId) {
          if (previousUserIdRef.current !== userId) {
            if (previousUserIdRef.current !== null) {
              queryClient.removeQueries({ queryKey: ['team-usage'] });
            }
            previousUserIdRef.current = userId;
            setEffectiveUserId(userId);
          }
        } else if (mounted && !userId) {
          previousUserIdRef.current = null;
          setEffectiveUserId(null);
        }
      } catch (error) {
        logger.error('Error resolving effective user ID:', error);
        if (mounted) {
          previousUserIdRef.current = null;
          setEffectiveUserId(null);
        }
      }
    };

    resolveUserId();

    // Listen only for sign-in/sign-out to reset — NOT TOKEN_REFRESHED (avoids restart cascade)
    const { data: { subscription: authSubscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
        if (mounted) {
          const newUserId = session?.user?.id || null;
          if (previousUserIdRef.current !== newUserId) {
            logger.debug('Auth state changed, updating effective user ID', { event });
            queryClient.removeQueries({ queryKey: ['team-usage'] });
            previousUserIdRef.current = newUserId;
            setEffectiveUserId(newUserId);
          }
        }
      }
    });

    return () => {
      mounted = false;
      authSubscription.unsubscribe();
    };
  }, [isImpersonating, impersonatedUser?.id, queryClient]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['team-usage', effectiveUserId],
    queryFn: () => {
      // Trust the effectiveUserId from query key — already verified in the useEffect
      return fetchTeamData(effectiveUserId!);
    },
    enabled: !!effectiveUserId,
    staleTime: 1000 * 60 * 2,
    gcTime: 1000 * 60 * 5,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    placeholderData: (previousData) => previousData,
  });

  const incrementUsageMutation = useMutation({
    mutationFn: async ({ type, amount = 1 }: { type: 'norths' | 'ai_summaries'; amount?: number }) => {
      // Usage is calculated in real-time, so we just refresh the data
      // This function is kept for API compatibility but doesn't persist to database
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['team-usage'] });
    },
  });

  // Entitlement is evaluated from status + period end, not plan_tier alone.
  // Optimistic while loading so a paying customer never sees a paywall flash.
  const entitlement = evaluateEntitlement(data?.subscription);
  const isEntitled = isLoading ? true : entitlement.entitled;

  const checkLimit = (type: 'norths' | 'team_members'): boolean => {
    if (!data?.planLimits || !data?.usage) return false;
    if (!isEntitled) return false;

    switch (type) {
      case 'norths':
        if (data.planLimits.max_active_norths === null) return true;
        return data.usage.norths_created < data.planLimits.max_active_norths;

      case 'team_members':
        return true;

      default:
        return false;
    }
  };

  const canCreateCMA = (): boolean => {
    if (!data?.planLimits || !data?.usage) return true;
    if (!isEntitled) return false;
    if (data.planLimits.max_cmas_per_month === null) return true;
    return data.usage.cmas_this_month < data.planLimits.max_cmas_per_month;
  };

  const canCreateBuyerReport = (): boolean => {
    if (!data?.planLimits || !data?.usage) return false;
    if (!isEntitled) return false;
    if (data.planLimits.max_property_reports_per_month == null) return true; // unlimited
    if (data.planLimits.max_property_reports_per_month === 0) return false;  // locked (0 = no access)
    return data.usage.property_reports_this_month < data.planLimits.max_property_reports_per_month;
  };

  const getUsagePercentage = (type: 'norths'): number => {
    if (!data?.planLimits || !data?.usage) return 0;

    switch (type) {
      case 'norths':
        if (data.planLimits.max_active_norths === null) return 0;
        return (data.usage.norths_created / data.planLimits.max_active_norths) * 100;

      default:
        return 0;
    }
  };

  const showUpgradePrompt = (feature: string) => {
    setUpgradeFeature(feature);
    setUpgradeDialogOpen(true);
  };

  return {
    loading: isLoading || !effectiveUserId,
    error,
    teamId: data?.teamId || null,
    planLimits: data?.planLimits || null,
    usage: data?.usage || null,
    subscription: data?.subscription || null,
    isEntitled,
    entitlement,
    checkLimit,
    canCreateCMA,
    canCreateBuyerReport,
    incrementUsage: (type: 'norths', amount = 1) =>
      incrementUsageMutation.mutate({ type, amount }),
    getUsagePercentage,
    refreshUsage: refetch,
    showUpgradePrompt,
    upgradeDialogOpen,
    setUpgradeDialogOpen,
    upgradeFeature,
  };
}
